import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  SkyButton,
  SkyDivider,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
} from "@skypointcloud/skypoint-ui";
import { signalAppReady } from "../../../utils/appLoadingSignal";
import { signInProviders } from "./signInProviders";
import { countryOptions } from "./countryOptions";
import {
  checkCode,
  finishPhoneSignIn,
  loadPhoneSignInSettings,
  requestCode,
  type PhoneSignInSettings,
} from "./phoneSignInApi";
import type { SignInPageProps } from "./SignInPage.interface";

/**
 * The sign-in page, after skySchedule's: a button per sign-in provider, then,
 * when it's turned on, signing in with a mobile number (a texted code).
 */
export function SignInPage({ onProviderSignIn, providerError, providerBusy }: SignInPageProps) {
  const providers = useMemo(() => signInProviders(), []);
  const [settings, setSettings] = useState<PhoneSignInSettings | null>(null);
  const [step, setStep] = useState<"number" | "code">("number");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // A pass from a code that checked out, kept while starting the session: the
  // code is spent, so trying again retries only the session, never the code.
  const [pass, setPass] = useState<string | null>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  // The right keypad on phones (SkyInput takes no inputMode), and the focus
  // carried to the code box when it replaces the number form.
  useEffect(() => {
    phoneRef.current?.setAttribute("inputmode", "tel");
    if (step === "code" && codeRef.current) {
      codeRef.current.setAttribute("inputmode", "numeric");
      codeRef.current.focus();
    }
  }, [step, settings]);

  useEffect(() => {
    // The startup overlay covers the app until something says it's ready.
    signalAppReady();
    let live = true;
    void loadPhoneSignInSettings().then((loaded) => {
      if (!live) return;
      setSettings(loaded);
      setCountry(loaded.countries[0]?.code ?? "");
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const countryChoices = useMemo(() => countryOptions(settings?.countries ?? []), [settings]);
  const phoneOn = settings?.enabled === true;
  const codeLength = settings?.codeLength ?? 6;
  const callingCode = settings?.countries.find((c) => c.code === country)?.callingCode ?? "";
  // A number typed with its own "+code" is shown as typed, not after the picked one.
  const shownNumber = phone.trim().startsWith("+")
    ? phone.trim()
    : `+${callingCode} ${phone.trim()}`;
  const shownError = error ?? providerError ?? null;

  const sendCode = async () => {
    if (sending) return;
    if (!phone.trim()) {
      setError("Enter your mobile number.");
      return;
    }
    setSending(true);
    setError(null);
    const result = await requestCode(phone.trim(), country);
    setSending(false);
    if (!result.ok) {
      setError(result.error);
      // "Too many codes": Resend waits as long as the server says.
      if (result.retryIn) setResendIn(Math.ceil(result.retryIn));
      return;
    }
    setStep("code");
    setCode("");
    setPass(null);
    setResendIn(result.resendIn);
  };

  const onSendCode = (e: FormEvent) => {
    e.preventDefault();
    void sendCode();
  };

  const onCheckCode = async (e: FormEvent) => {
    e.preventDefault();
    if (checking) return;
    const typed = code.replace(/\s/g, "");
    if (typed.length !== codeLength) {
      setError(`Enter the ${codeLength}-digit code from the text.`);
      return;
    }
    setChecking(true);
    setError(null);
    let token = pass;
    if (!token) {
      const result = await checkCode(phone.trim(), country, typed);
      if (!result.ok) {
        setChecking(false);
        setError(result.error);
        if (result.askAgain) {
          setStep("number");
          setCode("");
        }
        return;
      }
      token = result.token;
      setPass(token);
    }
    const failed = await finishPhoneSignIn(token);
    if (failed) {
      setChecking(false);
      setError(failed);
    }
    // Otherwise the page is loading the app; stay busy until it does.
  };

  const useAnotherNumber = () => {
    setStep("number");
    setCode("");
    setPass(null);
    // The wait is for the number just used, not another one.
    setResendIn(0);
    setError(null);
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-b from-white to-orange-50 px-4 py-10">
      <main className="w-full max-w-[440px] rounded-2xl border border-neutral-200 bg-white px-6 py-8 shadow-sm sm:px-10">
        <div className="flex flex-col items-center text-center">
          <img
            src="/dist/img/skySupport-logo.png"
            alt="skySupport"
            className="h-12 w-auto"
          />
          <h1 className="mt-6 text-2xl font-bold text-neutral-900">Welcome!</h1>
          <p className="mt-1 text-sm text-neutral-500">Sign in to your account</p>
        </div>

        {shownError && (
          <div
            className="mt-6"
            role="alert"
          >
            <SkyMessageBar
              intent="error"
              layout="multiline"
              message={shownError}
            />
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3">
          {providers.map((p) => (
            <SkyButton
              key={p.id}
              type="button"
              appearance="outline"
              size="large"
              icon={p.icon}
              className="w-full justify-center"
              disabled={providerBusy || checking}
              onClick={() => {
                setError(null);
                onProviderSignIn(p.domainHint);
              }}
            >
              {p.label}
            </SkyButton>
          ))}
        </div>

        {phoneOn && settings && (
          <>
            <SkyDivider
              appearance="subtle"
              className="my-6"
            >
              OR
            </SkyDivider>

            {step === "number" ? (
              <form
                onSubmit={onSendCode}
                className="flex flex-col gap-4"
                aria-label="Sign in with your phone"
                noValidate
              >
                <div className="flex gap-2">
                  {settings.countries.length > 1 && (
                    <div className="w-[130px] shrink-0">
                      <SkyDropdown
                        label="Country"
                        aria-label="Country"
                        options={countryChoices}
                        listboxMinWidth="260px"
                        selectedOptions={country ? [country] : []}
                        onOptionSelect={(values) => {
                          if (values[0]) setCountry(values[0]);
                        }}
                        size="large"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <SkyInput
                      label="Mobile number"
                      type="text"
                      name="phone"
                      ref={phoneRef}
                      autoComplete="tel-national"
                      placeholder="Phone number"
                      value={phone}
                      maxLength={20}
                      prefix={settings.countries.length > 1 ? undefined : `+${callingCode}`}
                      size="large"
                      onChange={(e) => {
                        setPhone(e.target.value.replace(/[^\d\s()+-]/g, ""));
                        // A wait ("too many codes") is for the number that hit it.
                        if (resendIn > 0) setResendIn(0);
                        if (error) setError(null);
                      }}
                      className="w-full"
                    />
                  </div>
                </div>
                <SkyButton
                  type="submit"
                  appearance="primary"
                  size="large"
                  className="w-full justify-center"
                  loading={sending}
                  disabled={sending || !phone.trim() || providerBusy || resendIn > 0}
                >
                  {resendIn > 0 ? `Send code in ${resendIn}s` : "Send code"}
                </SkyButton>
              </form>
            ) : (
              <form
                onSubmit={onCheckCode}
                className="flex flex-col gap-4"
                aria-label="Enter your code"
                noValidate
              >
                <p className="text-sm text-neutral-600">
                  Enter the {codeLength}-digit code we texted to{" "}
                  <strong className="text-neutral-900">{shownNumber}</strong>.
                </p>
                <SkyInput
                  label="Code"
                  type="text"
                  name="code"
                  ref={codeRef}
                  autoComplete="one-time-code"
                  placeholder={"0".repeat(codeLength)}
                  value={code}
                  size="large"
                  onChange={(e) => {
                    setCode(e.target.value.replace(/\D/g, "").slice(0, codeLength));
                    if (error) setError(null);
                  }}
                  className="w-full"
                />
                <SkyButton
                  type="submit"
                  appearance="primary"
                  size="large"
                  className="w-full justify-center"
                  loading={checking}
                  disabled={checking || code.length !== codeLength}
                >
                  Sign in
                </SkyButton>
                <div className="flex items-center justify-between gap-2">
                  <SkyButton
                    type="button"
                    appearance="subtle"
                    size="small"
                    disabled={resendIn > 0 || sending || checking}
                    loading={sending}
                    onClick={() => void sendCode()}
                  >
                    {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                  </SkyButton>
                  <SkyButton
                    type="button"
                    appearance="subtle"
                    size="small"
                    disabled={checking}
                    onClick={useAnotherNumber}
                  >
                    Use a different number
                  </SkyButton>
                </div>
              </form>
            )}
          </>
        )}

        <p className="mt-8 text-center text-xs text-neutral-500">
          By signing in, you accept our{" "}
          <a
            href="https://skypointcloud.com/terms/"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            terms and conditions
          </a>{" "}
          and confirm that you have taken note of our{" "}
          <a
            href="https://privacy.skypoint.ai/"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            privacy policy
          </a>
          .
        </p>
      </main>
    </div>
  );
}
